export type BrazilState = {
  id: string;
  code: string;
  name: string;
};

export type BrazilMunicipality = {
  id: string;
  name: string;
  state: string;
  latitude?: number;
  longitude?: number;
  kind?: "municipality" | "administrative-region";
};

export type GeoPoint = { latitude: number; longitude: number; accuracy?: number };

export type CommercialPlaceType = "ceasa" | "feira" | "outro";

export type CommercialPlace = {
  id: string;
  name: string;
  type: Exclude<CommercialPlaceType, "outro">;
  state: string;
  municipality: string;
  municipalityCode?: string;
  showInQuickFilter?: boolean;
  source: "Conab/Prohort" | "GDF" | "Administração Terra Boa";
};

export type LocationCatalogEntry = {
  id: string;
  kind: "municipality" | "commercial-place";
  name: string;
  state: string;
  stateName: string;
  municipality: string;
  municipalityCode: string;
  latitude?: number;
  longitude?: number;
  placeType?: "ceasa" | "feira";
  showInQuickFilter?: boolean;
  sourceReference: string;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type BrazilLocation = {
  state: string;
  stateName: string;
  municipality: string;
  municipalityCode: string;
  commercialPlaceId: string;
  commercialPlaceName: string;
  commercialPlaceType: CommercialPlaceType | "";
};

export const EMPTY_BRAZIL_LOCATION: BrazilLocation = {
  state: "",
  stateName: "",
  municipality: "",
  municipalityCode: "",
  commercialPlaceId: "",
  commercialPlaceName: "",
  commercialPlaceType: "",
};

export const BRAZIL_STATES: BrazilState[] = [
  ["12", "AC", "Acre"], ["27", "AL", "Alagoas"], ["16", "AP", "Amapá"], ["13", "AM", "Amazonas"],
  ["29", "BA", "Bahia"], ["23", "CE", "Ceará"], ["53", "DF", "Distrito Federal"], ["32", "ES", "Espírito Santo"],
  ["52", "GO", "Goiás"], ["21", "MA", "Maranhão"], ["51", "MT", "Mato Grosso"], ["50", "MS", "Mato Grosso do Sul"],
  ["31", "MG", "Minas Gerais"], ["15", "PA", "Pará"], ["25", "PB", "Paraíba"], ["41", "PR", "Paraná"],
  ["26", "PE", "Pernambuco"], ["22", "PI", "Piauí"], ["33", "RJ", "Rio de Janeiro"], ["24", "RN", "Rio Grande do Norte"],
  ["43", "RS", "Rio Grande do Sul"], ["11", "RO", "Rondônia"], ["14", "RR", "Roraima"], ["42", "SC", "Santa Catarina"],
  ["35", "SP", "São Paulo"], ["28", "SE", "Sergipe"], ["17", "TO", "Tocantins"],
].map(([id, code, name]) => ({ id, code, name }));

export const TERRITORY_SOURCE = "https://servicodados.ibge.gov.br/api/v1/localidades/municipios";
export const TERRITORY_GENERATED_AT = "2026-09-02T01:59:35.209Z";
export const TERRITORY_COORDINATE_SOURCE = "https://raw.githubusercontent.com/kelvins/Municipios-Brasileiros/main/csv/municipios.csv";
export const DF_LOCALITY_SOURCE = "https://www.geoservicos.ide.df.gov.br/arcgis/rest/services/Publico/LIMITES/FeatureServer/1/query";

let brazilMunicipalities: BrazilMunicipality[] = [];
let brazilMunicipalitiesPromise: Promise<BrazilMunicipality[]> | null = null;
let brazilMunicipalitiesById = new Map<string, BrazilMunicipality>();
let brazilMunicipalitiesByState = new Map<string, BrazilMunicipality[]>();
let brazilMunicipalitiesByName = new Map<string, BrazilMunicipality>();

function indexBrazilMunicipalities(municipalities: BrazilMunicipality[]) {
  brazilMunicipalitiesById = new Map();
  brazilMunicipalitiesByState = new Map();
  brazilMunicipalitiesByName = new Map();
  for (const municipality of municipalities) {
    brazilMunicipalitiesById.set(municipality.id, municipality);
    brazilMunicipalitiesByName.set(`${municipality.state}:${normalizeLocationText(municipality.name)}`, municipality);
    const stateMunicipalities = brazilMunicipalitiesByState.get(municipality.state) ?? [];
    stateMunicipalities.push(municipality);
    brazilMunicipalitiesByState.set(municipality.state, stateMunicipalities);
  }
  for (const stateMunicipalities of brazilMunicipalitiesByState.values()) {
    stateMunicipalities.sort((left, right) => left.name.localeCompare(right.name, "pt-BR"));
  }
}

export function loadBrazilMunicipalities() {
  if (brazilMunicipalities.length) return Promise.resolve(brazilMunicipalities);
  if (!brazilMunicipalitiesPromise) {
    brazilMunicipalitiesPromise = import("./terra-boa-municipalities.json")
      .then(({ default: territory }) => {
        brazilMunicipalities = territory.municipalities as BrazilMunicipality[];
        indexBrazilMunicipalities(brazilMunicipalities);
        return brazilMunicipalities;
      })
      .catch((error) => {
        brazilMunicipalitiesPromise = null;
        throw error;
      });
  }
  return brazilMunicipalitiesPromise;
}

const ceasas: CommercialPlace[] = ([
  ["ama-ba-juazeiro", "AMA/BA - Juazeiro", "BA", "Juazeiro"],
  ["ceagesp-aracatuba", "CEAGESP - Araçatuba", "SP", "Araçatuba"],
  ["ceagesp-araraquara", "CEAGESP - Araraquara", "SP", "Araraquara"],
  ["ceagesp-franca", "CEAGESP - Franca", "SP", "Franca"],
  ["ceagesp-presidente-prudente", "CEAGESP - Presidente Prudente", "SP", "Presidente Prudente"],
  ["ceagesp-ribeirao-preto", "CEAGESP - Ribeirão Preto", "SP", "Ribeirão Preto"],
  ["ceagesp-sao-jose-campos", "CEAGESP - São José dos Campos", "SP", "São José dos Campos"],
  ["ceagesp-sao-jose-rio-preto", "CEAGESP - São José do Rio Preto", "SP", "São José do Rio Preto"],
  ["ceagesp-sao-paulo", "CEAGESP - São Paulo", "SP", "São Paulo"],
  ["ceagesp-sorocaba", "CEAGESP - Sorocaba", "SP", "Sorocaba"],
  ["ceasa-al-maceio", "CEASA/AL - Maceió", "AL", "Maceió"],
  ["ceasa-ba-salvador", "CEASA/BA - Salvador", "BA", "Salvador"],
  ["ceasa-ce-fortaleza", "CEASA/CE - Fortaleza (Maracanaú)", "CE", "Maracanaú"],
  ["ceasa-df-brasilia", "CEASA/DF - Brasília", "DF", "Brasília"],
  ["ceasa-es-vitoria", "CEASA/ES - Vitória", "ES", "Vitória"],
  ["ceasa-go-goiania", "CEASA/GO - Goiânia", "GO", "Goiânia"],
  ["ceasa-ma-sao-luis", "CEASA/MA - São Luís", "MA", "São Luís"],
  ["ceasa-ms-campo-grande", "CEASA/MS - Campo Grande", "MS", "Campo Grande"],
  ["ceasa-mt-cuiaba", "CEASA/MT - Cuiabá", "MT", "Cuiabá"],
  ["ceasa-pa-belem", "CEASA/PA - Belém", "PA", "Belém"],
  ["ceasa-pb-joao-pessoa", "CEASA/PB - João Pessoa", "PB", "João Pessoa"],
  ["ceasa-pb-patos", "CEASA/PB - Patos", "PB", "Patos"],
  ["ceasa-pe-recife", "CEASA/PE - Recife", "PE", "Recife"],
  ["ceasa-pr-cascavel", "CEASA/PR - Cascavel", "PR", "Cascavel"],
  ["ceasa-pr-curitiba", "CEASA/PR - Curitiba", "PR", "Curitiba"],
  ["ceasa-pr-foz-iguacu", "CEASA/PR - Foz do Iguaçu", "PR", "Foz do Iguaçu"],
  ["ceasa-pr-maringa", "CEASA/PR - Maringá", "PR", "Maringá"],
  ["ceasa-rj-rio-janeiro", "CEASA/RJ - Rio de Janeiro", "RJ", "Rio de Janeiro"],
  ["ceasa-rn-natal", "CEASA/RN - Natal", "RN", "Natal"],
  ["ceasa-rs-caxias-sul", "CEASA/RS - Caxias do Sul", "RS", "Caxias do Sul"],
  ["ceasa-rs-porto-alegre", "CEASA/RS - Porto Alegre", "RS", "Porto Alegre"],
  ["ceasa-sc-grande-florianopolis", "CEASA/SC - Grande Florianópolis (São José)", "SC", "São José"],
  ["ceasa-sp-campinas", "CEASA/SP - Campinas", "SP", "Campinas"],
  ["ceasa-to-palmas", "CEASA/TO - Palmas", "TO", "Palmas"],
  ["ceasaminas-barbacena", "CeasaMinas - Barbacena", "MG", "Barbacena"],
  ["ceasaminas-grande-bh", "CeasaMinas - Grande BH (Contagem)", "MG", "Contagem"],
  ["ceasaminas-uberaba", "CeasaMinas - Uberaba", "MG", "Uberaba"],
] as Array<[string, string, string, string]>).map(([id, name, state, municipality]) => ({ id, name, state, municipality, type: "ceasa", source: "Conab/Prohort" }));

const dfFairs: CommercialPlace[] = ([
  ["feira-torre-tv", "Feira de Artesanato da Torre de TV"],
  ["feira-fecab-sia", "Feira da Cultura, Arte e Beleza - FECAB (SIA)"],
  ["feira-candangolandia", "Feira Permanente da Candangolândia"],
  ["feira-brazlandia", "Feira Permanente de Brazlândia"],
  ["feira-nucleo-bandeirante", "Feira Permanente Ibaneis Rocha Barros Pai (Núcleo Bandeirante)"],
  ["feira-sobradinho-ii", "Feira Permanente de Sobradinho II"],
  ["feira-cruzeiro", "Feira Permanente do Cruzeiro"],
  ["feira-estrutural", "Feira Permanente da Estrutural"],
  ["feira-paranoa", "Feira Permanente do Paranoá"],
  ["feira-sao-sebastiao", "Feira Permanente de São Sebastião"],
  ["feira-riacho-fundo-ii", "Feira Permanente do Riacho Fundo II"],
  ["feira-produtor-ceilandia", "Feira do Produtor de Ceilândia"],
  ["feira-central-ceilandia", "Feira Central de Ceilândia"],
  ["feira-setor-o", "Feira Permanente do Setor O"],
  ["feira-guariroba", "Feira Permanente da Guariroba"],
  ["feira-modelo-sobradinho", "Feira Modelo de Sobradinho"],
  ["feira-samambaia-202", "Feira Permanente da 202 (Samambaia)"],
  ["feira-samambaia-210", "Feira Permanente da 210 (Samambaia)"],
  ["feira-livre-samambaia-313", "Feira Livre da 313 (Samambaia)"],
] as Array<[string, string]>).map(([id, name]) => ({ id, name, state: "DF", municipality: "Brasília", type: "feira", source: "GDF" }));

export const COMMERCIAL_PLACES = [...ceasas, ...dfFairs];

const NORMALIZED_TEXT_CACHE_LIMIT = 12_000;
const normalizedTextCache = new Map<string, string>();

export function normalizeLocationText(value: string) {
  const cached = normalizedTextCache.get(value);
  if (cached !== undefined) return cached;
  const normalized = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLocaleLowerCase("pt-BR");
  if (normalizedTextCache.size >= NORMALIZED_TEXT_CACHE_LIMIT) {
    const oldest = normalizedTextCache.keys().next().value;
    if (oldest !== undefined) normalizedTextCache.delete(oldest);
  }
  normalizedTextCache.set(value, normalized);
  return normalized;
}

function editDistance(left: string, right: string) {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex];
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      current[rightIndex] = Math.min(
        current[rightIndex - 1] + 1,
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1),
      );
    }
    previous.splice(0, previous.length, ...current);
  }
  return previous[right.length];
}

export function fuzzyTextScore(source: string, query: string) {
  const normalizedQuery = normalizeLocationText(query);
  if (!normalizedQuery) return 0;
  const normalizedSource = normalizeLocationText(source);
  if (normalizedSource === normalizedQuery) return 0;
  if (normalizedSource.startsWith(normalizedQuery)) return 1;
  if (normalizedSource.includes(normalizedQuery)) return 2;
  const sourceWords = normalizedSource.split(/[^a-z0-9]+/).filter(Boolean);
  const queryWords = normalizedQuery.split(/[^a-z0-9]+/).filter(Boolean);
  let score = 10;
  for (const queryWord of queryWords) {
    let bestWordScore = Number.POSITIVE_INFINITY;
    const tolerance = queryWord.length >= 8 ? 2 : queryWord.length >= 4 ? 1 : 0;
    for (const sourceWord of sourceWords) {
      if (sourceWord === queryWord) bestWordScore = Math.min(bestWordScore, 0);
      else if (sourceWord.startsWith(queryWord)) bestWordScore = Math.min(bestWordScore, 1);
      else if (sourceWord.includes(queryWord)) bestWordScore = Math.min(bestWordScore, 2);
      else if (tolerance > 0 && Math.abs(sourceWord.length - queryWord.length) <= tolerance) {
        // Não aceite siglas curtas da UF contidas em termos maiores (ex.: GO em "goiania").
        // A tolerância permanece disponível para nomes e pequenas variações de digitação.
        const distance = editDistance(sourceWord, queryWord);
        if (distance <= tolerance) bestWordScore = Math.min(bestWordScore, 3 + distance);
      }
    }
    if (!Number.isFinite(bestWordScore)) return Number.POSITIVE_INFINITY;
    score += bestWordScore;
  }
  return score;
}

export function fuzzyTextMatches(source: string, query: string) {
  return Number.isFinite(fuzzyTextScore(source, query));
}

export function municipalitiesForState(state: string) {
  return brazilMunicipalitiesByState.get(state) ?? [];
}

export function catalogMunicipalities(entries: LocationCatalogEntry[]) {
  return entries.filter((entry) => entry.kind === "municipality").map((entry) => ({ id: entry.municipalityCode, name: entry.municipality, state: entry.state, latitude: entry.latitude, longitude: entry.longitude }));
}

export function catalogCommercialPlaces(entries: LocationCatalogEntry[]) {
  return entries.filter((entry) => entry.kind === "commercial-place" && entry.placeType).map((entry) => ({
    id: entry.id,
    name: entry.name,
    type: entry.placeType!,
    state: entry.state,
    municipality: entry.municipality,
    municipalityCode: entry.municipalityCode,
    showInQuickFilter: entry.showInQuickFilter === true,
    source: "Administração Terra Boa" as const,
  }));
}

export function allCommercialPlaces(additional: CommercialPlace[] = []) {
  const places = new Map(COMMERCIAL_PLACES.map((place) => [place.id, place]));
  for (const place of additional) places.set(place.id, { ...places.get(place.id), ...place });
  return [...places.values()];
}

export function allMunicipalities(additional: BrazilMunicipality[] = []) {
  if (!additional.length) return brazilMunicipalities;
  const municipalities = new Map(brazilMunicipalities.map((municipality) => [municipality.id, municipality]));
  for (const municipality of additional) {
    if (!municipalities.has(municipality.id)) municipalities.set(municipality.id, municipality);
  }
  return [...municipalities.values()];
}

export function municipalitiesForStateWithCatalog(state: string, additional: BrazilMunicipality[] = []) {
  const official = brazilMunicipalitiesByState.get(state) ?? [];
  const complementary = additional.filter((municipality) => municipality.state === state && !brazilMunicipalitiesById.has(municipality.id));
  if (!complementary.length) return official;
  return [...official, ...complementary].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

export function findMunicipality(state: string, value: string, additional: BrazilMunicipality[] = []) {
  const normalized = normalizeLocationText(value);
  return brazilMunicipalitiesByName.get(`${state}:${normalized}`)
    ?? additional.find((municipality) => municipality.state === state && normalizeLocationText(municipality.name) === normalized);
}

export function locationFromParts(location?: Partial<BrazilLocation> | null, additionalMunicipalities: BrazilMunicipality[] = []): BrazilLocation {
  if (!location) return { ...EMPTY_BRAZIL_LOCATION };
  const state = String(location.state ?? "").toUpperCase();
  const stateRecord = BRAZIL_STATES.find((item) => item.code === state);
  const municipalityCode = String(location.municipalityCode ?? "");
  const officialMunicipality = brazilMunicipalitiesById.get(municipalityCode);
  const municipalityByCode = officialMunicipality?.state === state
    ? officialMunicipality
    : additionalMunicipalities.find((item) => item.id === municipalityCode && item.state === state);
  const municipality = municipalityByCode ?? findMunicipality(state, String(location.municipality ?? ""), additionalMunicipalities);
  return {
    state,
    stateName: stateRecord?.name ?? String(location.stateName ?? ""),
    municipality: municipality?.name ?? String(location.municipality ?? ""),
    municipalityCode: municipality?.id ?? String(location.municipalityCode ?? ""),
    commercialPlaceId: String(location.commercialPlaceId ?? ""),
    commercialPlaceName: String(location.commercialPlaceName ?? ""),
    commercialPlaceType: location.commercialPlaceType ?? "",
  };
}

export function isValidBrazilLocation(location: BrazilLocation, additionalMunicipalities: BrazilMunicipality[] = []) {
  const municipality = brazilMunicipalitiesById.get(location.municipalityCode)
    ?? additionalMunicipalities.find((item) => item.id === location.municipalityCode);
  return Boolean(municipality && municipality.state === location.state && municipality.name === location.municipality);
}

export function isValidLocationFilter(location: BrazilLocation, additionalMunicipalities: BrazilMunicipality[] = []) {
  if (!location.state) return !location.municipality && !location.municipalityCode && !location.commercialPlaceId;
  if (!BRAZIL_STATES.some((state) => state.code === location.state)) return false;
  if (!location.municipality && !location.municipalityCode) return !location.commercialPlaceId;
  return isValidBrazilLocation(location, additionalMunicipalities);
}

export function coordinatesForLocation(location: Partial<BrazilLocation>, additionalMunicipalities: BrazilMunicipality[] = []) {
  const municipality = brazilMunicipalitiesById.get(String(location.municipalityCode ?? ""))
    ?? additionalMunicipalities.find((item) => item.id === location.municipalityCode)
    ?? findMunicipality(String(location.state ?? ""), String(location.municipality ?? ""), additionalMunicipalities);
  return municipality && Number.isFinite(municipality.latitude) && Number.isFinite(municipality.longitude)
    ? { latitude: municipality.latitude!, longitude: municipality.longitude! }
    : null;
}

export function distanceInKm(origin: GeoPoint, destination: GeoPoint) {
  const radians = (degrees: number) => degrees * Math.PI / 180;
  const latitudeDelta = radians(destination.latitude - origin.latitude);
  const longitudeDelta = radians(destination.longitude - origin.longitude);
  const firstLatitude = radians(origin.latitude);
  const secondLatitude = radians(destination.latitude);
  const haversine = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(firstLatitude) * Math.cos(secondLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

export function nearestMunicipality(point: GeoPoint, additionalMunicipalities: BrazilMunicipality[] = []) {
  return allMunicipalities(additionalMunicipalities).reduce<{ municipality: BrazilMunicipality; distance: number } | null>((nearest, municipality) => {
    if (!Number.isFinite(municipality.latitude) || !Number.isFinite(municipality.longitude)) return nearest;
    const distance = distanceInKm(point, { latitude: municipality.latitude!, longitude: municipality.longitude! });
    return !nearest || distance < nearest.distance ? { municipality, distance } : nearest;
  }, null);
}

export function commercialPlacesFor(location: Pick<BrazilLocation, "state" | "municipality">, additional: CommercialPlace[] = []) {
  const municipality = normalizeLocationText(location.municipality);
  return allCommercialPlaces(additional)
    .filter((place) => place.state === location.state && normalizeLocationText(place.municipality) === municipality)
    .sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name, "pt-BR"));
}

export function commercialPlaceMatches(candidate: Partial<BrazilLocation>, selected: Partial<BrazilLocation>) {
  if (!selected.commercialPlaceId && !selected.commercialPlaceName) return true;
  if (candidate.commercialPlaceId && selected.commercialPlaceId && candidate.commercialPlaceId === selected.commercialPlaceId) return true;
  const candidateName = normalizeLocationText(String(candidate.commercialPlaceName ?? ""));
  const selectedName = normalizeLocationText(String(selected.commercialPlaceName ?? ""));
  if (candidateName && selectedName && candidateName === selectedName) return true;
  return candidate.commercialPlaceType === "ceasa"
    && selected.commercialPlaceType === "ceasa"
    && candidate.state === selected.state
    && normalizeLocationText(String(candidate.municipality ?? "")) === normalizeLocationText(String(selected.municipality ?? ""));
}

export function formatBrazilLocation(location: Partial<BrazilLocation>, includePlace = true) {
  const city = location.municipality && location.state ? `${location.municipality}, ${location.state}` : "Localização não definida";
  return includePlace && location.commercialPlaceName ? `${location.commercialPlaceName} · ${city}` : city;
}

export function customCommercialPlaceId(type: CommercialPlaceType, name: string) {
  const slug = normalizeLocationText(name).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 70);
  return slug ? `custom-${type}-${slug}` : "";
}

export function locationMatches(candidate: Partial<BrazilLocation>, selected: BrazilLocation, origin?: GeoPoint | null, radiusKm?: number, additionalMunicipalities: BrazilMunicipality[] = []) {
  if (origin && radiusKm) {
    const coordinates = coordinatesForLocation(candidate, additionalMunicipalities);
    if (!coordinates || distanceInKm(origin, coordinates) > radiusKm) return false;
    return commercialPlaceMatches(candidate, selected);
  }
  if (!selected.state) return true;
  if (candidate.state !== selected.state) return false;
  if (!selected.municipalityCode) return true;
  const municipalityMatches = String(candidate.municipalityCode ?? "") === selected.municipalityCode
    || (candidate.state === selected.state
      && normalizeLocationText(String(candidate.municipality ?? "")) === normalizeLocationText(selected.municipality));
  if (!municipalityMatches) return false;
  return commercialPlaceMatches(candidate, selected);
}
